import { useEffect, useState, useCallback } from 'react';
import { api } from '@/lib/api';
import { Card } from '@/components/ui/card';
import { Lock, Delete, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const PAD = ['1', '2', '3', '4', '5', '6', '7', '8', '9'];

/**
 * Generic step-up PIN gate for a sensitive area (e.g. "batcave", "batmobile").
 * Calls /auth/area-verify with the given area and lifts the step-up token up.
 */
export function AreaPinGate({ area, title = 'Zone sécurisée', subtitle, icon: Icon = Lock, onUnlock }) {
  const [pin, setPin] = useState('');
  const [shake, setShake] = useState(false);
  const [loading, setLoading] = useState(false);

  const submit = useCallback(async (code) => {
    setLoading(true);
    try {
      const r = await api.post('/auth/area-verify', { area, pin: code });
      onUnlock(r.data.step_up_token);
    } catch (e) {
      setShake(true);
      setTimeout(() => setShake(false), 500);
      setPin('');
      toast.error(e?.response?.data?.detail || 'PIN incorrect');
    } finally {
      setLoading(false);
    }
  }, [area, onUnlock]);

  useEffect(() => {
    if (pin.length === 6 && !loading) submit(pin);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pin]);

  const press = (d) => { if (!loading) setPin((p) => (p.length >= 6 ? p : p + d)); };
  const backspace = () => { if (!loading) setPin((p) => p.slice(0, -1)); };

  return (
    <div className="min-h-[70vh] flex items-center justify-center p-6">
      <Card className="card-flat p-10 max-w-sm w-full" data-testid={`${area}-pin-gate`}>
        <div className="flex flex-col items-center mb-8">
          <div className="w-12 h-12 bg-zinc-950 rounded-md flex items-center justify-center mb-4">
            <Icon className="h-5 w-5 text-white" />
          </div>
          <h1 className="font-display text-2xl font-bold tracking-tight">{title}</h1>
          <p className="text-sm text-zinc-500 mt-1 text-center">{subtitle || 'Saisissez le code d\'accès'}</p>
        </div>
        <div className={`flex justify-center gap-3 mb-8 transition-transform ${shake ? 'animate-[shake_0.4s_ease-in-out]' : ''}`} data-testid={`${area}-pin-dots`}>
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className={`w-3.5 h-3.5 rounded-full transition-all ${i < pin.length ? 'bg-zinc-950 scale-110' : 'bg-zinc-200'}`} />
          ))}
        </div>
        <div className="grid grid-cols-3 gap-2.5">
          {PAD.map((d) => (
            <button key={d} type="button" onClick={() => press(d)} disabled={loading}
              data-testid={`${area}-pin-${d}`}
              className="h-14 rounded-md text-2xl font-semibold bg-white border border-zinc-200 hover:bg-zinc-100 active:scale-95 transition disabled:opacity-50">{d}</button>
          ))}
          <div />
          <button type="button" onClick={() => press('0')} disabled={loading}
            data-testid={`${area}-pin-0`}
            className="h-14 rounded-md text-2xl font-semibold bg-white border border-zinc-200 hover:bg-zinc-100 active:scale-95 transition disabled:opacity-50">0</button>
          <button type="button" onClick={backspace} disabled={loading || !pin.length}
            data-testid={`${area}-pin-backspace`}
            className="h-14 rounded-md bg-white border border-zinc-200 hover:bg-zinc-100 active:scale-95 transition disabled:opacity-30 flex items-center justify-center">
            <Delete className="h-4 w-4" />
          </button>
        </div>
        {loading && (
          <div className="mt-6 flex items-center justify-center text-zinc-400 text-sm">
            <Loader2 className="h-4 w-4 animate-spin mr-2" /> Vérification…
          </div>
        )}
      </Card>
      <style>{`@keyframes shake {0%,100%{transform:translateX(0)}25%{transform:translateX(-6px)}75%{transform:translateX(6px)}}`}</style>
    </div>
  );
}
